import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi } from "../lib/api";
import {
  LogOut,
  DollarSign,
  ShoppingBag,
  PackageCheck,
  FileUp,
  RefreshCw,
  ChevronRight,
  Loader2,
  ShoppingCart,
  Send,
  Trash2,
  Plus,
  Sparkles,
  BarChart3,
  Download,
  Mail,
  MailOpen,
  MousePointerClick,
  AlertTriangle,
  ShieldCheck,
  KeyRound,
  Unlock,
  RotateCcw,
  Copy as CopyIcon,
  BellRing,
  Image as ImageIcon,
  Upload,
  X,
  Download as DownloadIcon,
  GripVertical,
  Eye,
  EyeOff,
  CalendarClock,
  Megaphone,
  Pencil,
  Trash,
} from "lucide-react";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { toast } from "sonner";
import { ProductCreateModal } from "../components/ProductCreateModal";
import { SecurityOpsPanel } from "../components/admin/SecurityOpsPanel";
import { ProductImageEditModal } from "../components/admin/ProductImageEditModal";
import { ProductScheduleModal } from "../components/admin/ProductScheduleModal";
import { MarketingTab } from "../components/admin/MarketingTab";
import { AuditLogTab } from "../components/admin/AuditLogTab";
import { buildAdminTabs, DEFAULT_TAB } from "./admin/tabsManifest";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [carts, setCarts] = useState({ carts: [], counts: {} });
  const [analytics, setAnalytics] = useState(null);
  const [pendingReminders, setPendingReminders] = useState({ pending: [], count: 0 });
  const [systemStatus, setSystemStatus] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [bounceRate, setBounceRate] = useState(null);
  const [reviewsData, setReviewsData] = useState({ reviews: [], counts: { pending: 0, approved: 0, rejected: 0 } });
  const [securityData, setSecurityData] = useState({ failures: [], locked_ips: [], window_seconds: 900, max_fails: 5 });
  const [deliveryHealth, setDeliveryHealth] = useState(null);
  const [generatedJwtSecret, setGeneratedJwtSecret] = useState(null);
  const [securityBusy, setSecurityBusy] = useState(null);
  const [imageEditTarget, setImageEditTarget] = useState(null); // product object to replace image for
  const [scheduleTarget, setScheduleTarget] = useState(null);   // product object to schedule
  const [loading, setLoading] = useState(true);
  const { canInstall, isInstalled, isIOS, install } = usePwaInstall();
  const [tab, setTab] = useState(DEFAULT_TAB);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [refulfillState, setRefulfillState] = useState({ open: false, pending: 0, alreadyDone: 0, busy: false });

  const load = async () => {
    setLoading(true);
    try {
      const [s, o, p, c, a, dr, sys, es, br, rv, sec, dh] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/orders"),
        adminApi.get("/admin/products"),
        adminApi.get("/admin/abandoned-carts"),
        adminApi.get("/admin/analytics/summary"),
        adminApi.get("/admin/download-reminders/pending"),
        adminApi.get("/admin/system-status"),
        adminApi.get("/admin/email-stats"),
        adminApi.get("/admin/bounce-rate"),
        adminApi.get("/admin/reviews"),
        adminApi.get("/admin/security/login-failures"),
        adminApi.get("/admin/delivery-health"),
      ]);
      setStats(s.data);
      setOrders(o.data.orders);
      setProducts(p.data.products);
      setCarts(c.data);
      setAnalytics(a.data);
      setPendingReminders(dr.data);
      setSystemStatus(sys.data);
      setEmailStats(es.data);
      setBounceRate(br.data);
      setReviewsData(rv.data);
      setSecurityData(sec.data);
      setDeliveryHealth(dh.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("aigl_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load dashboard.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("aigl_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("aigl_admin_token");
    navigate("/admin/login");
  };

  const sendDownloadReminder = async (orderId) => {
    setSendingReminder(orderId);
    try {
      const { data } = await adminApi.post(`/admin/orders/${orderId}/send-download-reminder`);
      if (data.sent) {
        toast.success("Reminder sent.");
      } else if (data.reason === "already_sent") {
        toast.message("Reminder already sent for this order.");
      } else if (data.reason === "already_downloaded") {
        toast.message("Customer already downloaded — skipped.");
      } else if (data.error) {
        toast.error(data.error.length > 80 ? data.error.slice(0, 80) + "…" : data.error);
      } else {
        toast.message(data.reason || "Reminder skipped.");
      }
      await load();
    } catch (e) {
      toast.error("Failed to send reminder.");
    } finally {
      setSendingReminder(null);
    }
  };

  const resetFailedReminders = async () => {
    if (!window.confirm(
      "Re-arm download reminders that MailerSend previously rejected? " +
      "They'll be re-attempted on the next 5-minute scheduler tick."
    )) return;
    setSecurityBusy("rearm");
    try {
      const { data } = await adminApi.post("/admin/download-reminders/reset-failed");
      if (data.reset_count > 0) {
        toast.success(`${data.reset_count} reminder(s) re-armed.`);
      } else {
        toast.message("No failed reminders to re-arm.");
      }
      await load();
    } catch (e) {
      toast.error("Failed to re-arm reminders.");
    } finally {
      setSecurityBusy(null);
    }
  };

  // Immediate rescue send — bypasses scheduler window/attempts guardrails.
  const rescueDeliveries = async () => {
    const total = deliveryHealth?.rescue_pending?.total ?? 0;
    if (total === 0) {
      toast.message("Queue is empty — nothing to rescue.");
      return;
    }
    const eta = Math.max(1, Math.round((total * 5) / 60));
    if (!window.confirm(
      `Send download-reminder emails to ${total} pending customer(s) NOW? ` +
      `This bypasses the 30-day scheduler window and max-attempts cap. ` +
      `Estimated time: ~${eta} min (5s throttle).`
    )) return;
    setSecurityBusy("rescue");
    try {
      const { data } = await adminApi.post("/admin/download-reminders/rescue");
      const skipped = data.skipped ?? 0;
      const failed = data.failed ?? 0;
      toast.success(
        `Rescue complete · sent ${data.sent ?? 0}` +
        (skipped ? ` · skipped ${skipped}` : "") +
        (failed ? ` · failed ${failed}` : "")
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rescue send failed.");
    } finally {
      setSecurityBusy(null);
    }
  };

  // ---------------- Security & Ops actions ----------------
  const reconcilePayPal = async () => {
    if (!window.confirm("Run PayPal reconciliation now? Safe to repeat — flushes stuck 'initiated' orders.")) return;
    setSecurityBusy("paypal");
    try {
      const { data } = await adminApi.post("/admin/paypal/reconcile");
      toast.success(`PayPal reconcile complete · captured ${data.captured ?? 0}, skipped ${data.skipped ?? 0}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal reconcile failed.");
    } finally {
      setSecurityBusy(null);
    }
  };

  const archivePending = async () => {
    if (!window.confirm(
      "Archive every pending cart and stuck transaction? Records are MOVED (not deleted) to archive collections and the scheduler will stop nudging them."
    )) return;
    setSecurityBusy("archive");
    try {
      const { data } = await adminApi.post("/admin/transactions/clear-pending");
      toast.success(`Archived ${data.archived_carts ?? 0} cart(s) + ${data.archived_transactions ?? 0} txn(s).`);
      await load();
    } catch (e) {
      toast.error("Archive pending failed.");
    } finally {
      setSecurityBusy(null);
    }
  };

  // Refulfill Pre-Order Customers — 2-step: fetch count, confirm, execute.
  const openRefulfillModal = async () => {
    try {
      const { data } = await adminApi.get("/admin/refulfill-preorder/preview");
      setRefulfillState({
        open: true,
        pending: data.pending || 0,
        alreadyDone: data.already_refulfilled || 0,
        busy: false,
      });
    } catch (e) {
      toast.error("Failed to load pre-order customer count.");
    }
  };

  const executeRefulfill = async () => {
    setRefulfillState((s) => ({ ...s, busy: true }));
    try {
      const { data } = await adminApi.post("/admin/refulfill-preorder");
      const failed = (data.failed || []).length;
      toast.success(
        `Re-fulfilled ${data.refulfilled ?? 0} customer(s)` +
        (failed ? ` · ${failed} failed (see logs)` : "")
      );
      setRefulfillState({ open: false, pending: 0, alreadyDone: 0, busy: false });
    } catch (e) {
      toast.error(e?.response?.data?.error || "Refulfill failed.");
      setRefulfillState((s) => ({ ...s, busy: false }));
    }
  };

  const resetLoginBucket = async (ip) => {
    const label = ip ? `IP ${ip}` : "ALL locked IPs";
    if (!window.confirm(`Unlock ${label}? Future login attempts from there won't be rate-limited until they fail again.`)) return;
    setSecurityBusy(`reset-${ip || "all"}`);
    try {
      const { data } = await adminApi.post("/admin/security/reset-login-bucket", { ip: ip || "*" });
      if (data.scope === "all") {
        toast.success(`Cleared ${data.cleared_ips} IP bucket(s).`);
      } else {
        toast.success(data.existed ? `Cleared bucket for ${data.cleared_ip}.` : `${data.cleared_ip} had no entries.`);
      }
      await load();
    } catch (e) {
      toast.error("Reset failed.");
    } finally {
      setSecurityBusy(null);
    }
  };

  const testPager = async () => {
    setSecurityBusy("test-pager");
    try {
      const { data } = await adminApi.post("/admin/security/test-pager");
      if (data.webhook_configured) {
        toast.success(data.message);
      } else {
        toast.warning(data.message, { duration: 7000 });
      }
    } catch (e) {
      toast.error("Test pager failed.");
    } finally {
      setSecurityBusy(null);
    }
  };


  const generateNewJwtSecret = async () => {
    if (!window.confirm(
      "Generate a new JWT_SECRET? This does NOT mutate .env directly — you'll be shown the new value to paste into Emergent's Environment Variables, then redeploy. All existing admin sessions (including yours) will be invalidated after redeploy."
    )) return;
    setSecurityBusy("jwt");
    try {
      const { data } = await adminApi.post("/admin/security/generate-jwt-secret");
      setGeneratedJwtSecret(data.new_jwt_secret);
      toast.success("New JWT_SECRET generated — copy + redeploy to rotate.");
    } catch (e) {
      toast.error("Failed to generate JWT secret.");
    } finally {
      setSecurityBusy(null);
    }
  };

  const saveImageUrl = async (productId, imageUrl) => {
    try {
      await adminApi.put(`/admin/products/${productId}/image`, { image_url: imageUrl });
      toast.success("Image updated.");
      setImageEditTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update image.");
    }
  };

  const uploadImageFile = async (productId, file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image must be under 4MB.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await adminApi.post(`/admin/products/${productId}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded.");
      setImageEditTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    }
  };

  const saveProductOrder = async (orderedIds) => {
    try {
      const { data } = await adminApi.post("/admin/products/reorder", { order: orderedIds });
      toast.success(`New order saved · ${data.applied} products.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save order.");
    }
  };

  const toggleProductVisibility = async (product) => {
    const nextVisible = product.visibility_status === "hidden" ? true : false;
    try {
      const { data } = await adminApi.put(`/admin/products/${product.id}/visibility`, { visible: nextVisible });
      toast.success(nextVisible ? `${product.name} is LIVE.` : `${product.name} is HIDDEN.`);
      await load();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to toggle visibility.");
    }
  };

  const saveSchedule = async (productId, launchAt, archiveAt) => {
    try {
      const { data } = await adminApi.put(`/admin/products/${productId}/visibility`, {
        launch_at: launchAt || "",
        archive_at: archiveAt || "",
      });
      toast.success(`Schedule saved · status: ${data.visibility_status}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save schedule.");
    }
  };


  const copyToClipboard = async (text, label = "Value") => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard.`);
        return;
      }
      throw new Error("clipboard API unavailable");
    } catch {
      // Fallback for restricted contexts (iframes, http, headless browsers).
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success(`${label} copied to clipboard.`);
        else toast.error("Copy failed — please select and copy manually.");
      } catch {
        toast.error("Copy failed — please select and copy manually.");
      }
    }
  };

  const approveReview = async (reviewId) => {
    try {
      await adminApi.post(`/admin/reviews/${reviewId}/approve`);
      toast.success("Review approved — now live on the storefront.");
      await load();
    } catch (e) {
      toast.error("Failed to approve review.");
    }
  };

  const rejectReview = async (reviewId) => {
    if (!window.confirm("Reject this review? It will be hidden from the storefront.")) return;
    try {
      await adminApi.post(`/admin/reviews/${reviewId}/reject`);
      toast.message("Review rejected.");
      await load();
    } catch (e) {
      toast.error("Failed to reject review.");
    }
  };

  const sendReviewPrompt = async (orderId) => {
    if (!window.confirm("Manually send a review-prompt email for this order?")) return;
    try {
      const { data } = await adminApi.post(`/admin/orders/${orderId}/send-review-prompt`);
      if (data.sent) toast.success("Review prompt sent.");
      else toast.message(data.reason || data.error || "Skipped.");
      await load();
    } catch (e) {
      toast.error("Failed to send review prompt.");
    }
  };

  const handleUpload = async (productId, files) => {
    const list = Array.isArray(files) ? files : [files];
    let okCount = 0;
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await adminApi.post(`/admin/products/${productId}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        okCount += 1;
      } catch (e) {
        const msg = e?.response?.data?.detail || `Upload failed: ${f.name}`;
        toast.error(msg);
      }
    }
    if (okCount > 0) {
      toast.success(`${okCount} file${okCount === 1 ? "" : "s"} uploaded.`);
      load();
    }
  };

  const handleDeleteFile = async (productId, fileId, filename) => {
    if (!window.confirm(`Delete "${filename}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/products/${productId}/files/${fileId}`);
      toast.success("File deleted.");
      load();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (
      !window.confirm(
        `Delete "${productName}"? This removes the product and all its files. This cannot be undone.`
      )
    )
      return;
    try {
      await adminApi.delete(`/admin/products/${productId}`);
      toast.success("Product deleted.");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Delete failed.";
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="w-6 h-6 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  const totalRevenueLine = stats
    ? Object.entries(stats.revenue_by_currency || {})
        .map(([c, v]) => `${c} ${v.toFixed(2)}`)
        .join(" / ") || "—"
    : "—";

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#F8FAFC]">
      {/* Top bar */}
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[#C5A059] flex items-center justify-center">
              <span className="font-display text-[#C5A059]">A</span>
            </div>
            <div>
              <div className="font-display text-lg">AIGL Admin</div>
              <div className="font-accent text-[10px] text-[#C5A059]">CONTROL PANEL</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            {!isInstalled && (canInstall || isIOS) && (
              <button
                data-testid="header-install-app-btn"
                onClick={async () => {
                  if (isIOS) {
                    toast.message("On iOS: tap Share → 'Add to Home Screen'.", { duration: 6000 });
                    return;
                  }
                  const { outcome } = await install();
                  if (outcome === "accepted") toast.success("Installing AIGL Admin…");
                  else if (outcome === "dismissed") toast.message("Install dismissed.");
                }}
                className="text-xs font-accent text-[#0A192F] bg-[#C5A059] hover:bg-[#C5A059]/90 flex items-center gap-1.5 px-3 py-2"
              >
                <DownloadIcon className="w-3.5 h-3.5" /> INSTALL APP
              </button>
            )}
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="text-xs font-accent text-white/80 hover:text-[#C5A059] flex items-center gap-1.5 px-3 py-2 border border-white/10"
            >
              <RefreshCw className="w-3.5 h-3.5" /> REFRESH
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="text-xs font-accent text-white/80 hover:text-[#C5A059] flex items-center gap-1.5 px-3 py-2 border border-white/10"
            >
              <LogOut className="w-3.5 h-3.5" /> LOGOUT
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* Stat Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          <StatCard
            label="TOTAL ORDERS"
            value={stats?.total_orders ?? 0}
            icon={ShoppingBag}
            testid="stat-total-orders"
          />
          <StatCard
            label="PAID ORDERS"
            value={stats?.paid_orders ?? 0}
            icon={PackageCheck}
            testid="stat-paid-orders"
          />
          <StatCard
            label="REVENUE"
            value={totalRevenueLine}
            icon={DollarSign}
            testid="stat-revenue"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-6 mb-6 border-b border-slate-200">
          {buildAdminTabs({ reviewsData, cartsData: carts, securityData }).map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`pb-3 font-accent text-xs ${
                tab === t.id
                  ? "text-[#0A192F] border-b-2 border-[#C5A059]"
                  : "text-slate-500 hover:text-[#0A192F]"
              }`}
            >
              {t.label.toUpperCase()}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div className="space-y-4">
            {/* Fulfillment ops strip */}
            <div className="flex items-center justify-between bg-white border border-slate-200 px-5 py-3">
              <div className="flex items-center gap-2 text-slate-600">
                <PackageCheck size={14} className="text-[#C5A059]" />
                <span className="text-xs">
                  Post-launch fulfillment actions
                </span>
              </div>
              <button
                data-testid="refulfill-preorder-btn"
                onClick={openRefulfillModal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A192F] text-white text-[11px] font-accent tracking-wider border border-[#C5A059] hover:border-[#D4AF37] hover:shadow-[0_0_0_1px_#D4AF37] transition-shadow"
              >
                <Send size={12} className="text-[#D4AF37]" />
                RE-FULFILL PRE-ORDER CUSTOMERS
              </button>
            </div>

            <div data-testid="orders-table" className="bg-white border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-500">
                  <th className="px-5 py-3 font-accent text-[10px]">DATE</th>
                  <th className="px-5 py-3 font-accent text-[10px]">PROVIDER</th>
                  <th className="px-5 py-3 font-accent text-[10px]">PRODUCT</th>
                  <th className="px-5 py-3 font-accent text-[10px]">CUSTOMER</th>
                  <th className="px-5 py-3 font-accent text-[10px]">AMOUNT</th>
                  <th className="px-5 py-3 font-accent text-[10px]">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-5 py-12 text-center text-slate-400">
                      No orders yet.
                    </td>
                  </tr>
                )}
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-slate-100">
                    <td className="px-5 py-3 text-slate-600">
                      {new Date(o.created_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-slate-700 uppercase text-xs">{o.provider}</td>
                    <td className="px-5 py-3 text-[#0A192F] font-medium">{o.product_name}</td>
                    <td className="px-5 py-3 text-slate-600">
                      <div>{o.customer_email || "—"}</div>
                      <div className="text-xs text-slate-400">{o.customer_name}</div>
                    </td>
                    <td className="px-5 py-3 text-[#0A192F] font-semibold">
                      {o.amount?.toFixed(2)} {o.currency}
                    </td>
                    <td className="px-5 py-3">
                      <StatusPill status={o.payment_status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {tab === "analytics" && (
          <AnalyticsPanel
            data={analytics}
            loading={loading}
            pending={pendingReminders}
            onSendReminder={sendDownloadReminder}
            sendingReminder={sendingReminder}
            onResetFailed={resetFailedReminders}
          />
        )}

        {tab === "products" && (
          <div data-testid="products-management" className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Manage product files. New categories created here appear instantly on the landing page.
              </p>
              <button
                data-testid="open-create-product"
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] font-bold px-4 py-2.5 text-xs font-accent transition"
              >
                <Plus className="w-3.5 h-3.5" />
                NEW PRODUCT
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {products.map((p) => (
              <div key={p.id} className="bg-white border border-slate-200 p-6" data-testid={`product-card-admin-${p.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-display text-lg text-[#0A192F]">{p.name}</h4>
                    <p className="text-xs text-slate-500">{p.category}</p>
                  </div>
                  {p.file_count > 0 ? (
                    <span className="font-accent text-[10px] text-[#10B981]">
                      {p.file_count} FILE{p.file_count === 1 ? "" : "S"}
                    </span>
                  ) : (
                    <span className="font-accent text-[10px] text-slate-400">PLACEHOLDER</span>
                  )}
                </div>

                {/* File list */}
                <div className="mt-4 space-y-2" data-testid={`files-list-${p.id}`}>
                  {(p.files || []).length === 0 && (
                    <p className="text-xs text-slate-500">
                      No files uploaded. Customers receive an auto-generated placeholder PDF.
                    </p>
                  )}
                  {(p.files || []).map((f) => (
                    <div
                      key={f.id}
                      data-testid={`file-row-${f.id}`}
                      className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-[#0A192F] truncate" title={f.filename}>
                          {f.filename}
                        </div>
                        <div className="text-[10px] font-accent text-slate-500 mt-0.5">
                          {(f.size_bytes / 1024 / 1024).toFixed(2)} MB · uploaded{" "}
                          {new Date(f.uploaded_at).toLocaleString()}
                        </div>
                      </div>
                      <button
                        data-testid={`delete-file-${f.id}`}
                        onClick={() => handleDeleteFile(p.id, f.id, f.filename)}
                        className="text-rose-600 hover:text-white hover:bg-rose-600 border border-rose-300 hover:border-rose-600 p-1.5 transition"
                        title="Delete file"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                <label
                  htmlFor={`upload-${p.id}`}
                  className="mt-5 inline-flex items-center gap-2 cursor-pointer bg-[#0A192F] hover:bg-[#112240] text-white text-xs font-accent px-4 py-2.5 transition"
                  data-testid={`upload-pdf-${p.id}`}
                >
                  <FileUp className="w-4 h-4" />
                  ADD FILES (PDF or ZIP)
                </label>
                <input
                  id={`upload-${p.id}`}
                  type="file"
                  accept="application/pdf,application/zip,.pdf,.zip"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const fs = Array.from(e.target.files || []);
                    if (fs.length > 0) handleUpload(p.id, fs);
                    e.target.value = ""; // reset so the same file can be re-selected
                  }}
                />
                <p className="text-[10px] text-slate-400 mt-2">
                  Up to 50&nbsp;MB per file. 1 file → delivered as-is. 2+ files → bundled as ZIP.
                </p>
                {p.is_custom && (
                  <button
                    data-testid={`delete-product-${p.id}`}
                    onClick={() => handleDeleteProduct(p.id, p.name)}
                    className="mt-4 text-[11px] font-accent text-rose-600 hover:text-white hover:bg-rose-600 border border-rose-300 hover:border-rose-600 px-3 py-1.5 transition inline-flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3 h-3" />
                    DELETE PRODUCT
                  </button>
                )}
              </div>
            ))}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <ReviewsPanel
            data={reviewsData}
            onApprove={approveReview}
            onReject={rejectReview}
            onSendPrompt={sendReviewPrompt}
            orders={orders}
          />
        )}

        {tab === "system" && (
          <SystemPanel data={systemStatus} loading={loading} onReload={load} emailStats={emailStats} bounceRate={bounceRate} />
        )}

        {tab === "security" && (
          <SecurityOpsPanel
            data={securityData}
            products={products}
            busy={securityBusy}
            generatedSecret={generatedJwtSecret}
            deliveryHealth={deliveryHealth}
            onDismissSecret={() => setGeneratedJwtSecret(null)}
            onReload={load}
            onReArmReminders={resetFailedReminders}
            onRescueDeliveries={rescueDeliveries}
            onReconcilePayPal={reconcilePayPal}
            onArchivePending={archivePending}
            onResetBucket={resetLoginBucket}
            onGenerateJwt={generateNewJwtSecret}
            onTestPager={testPager}
            onCopy={copyToClipboard}
            onEditImage={(p) => setImageEditTarget(p)}
            onSaveOrder={saveProductOrder}
            onToggleVisibility={toggleProductVisibility}
            onScheduleProduct={(p) => setScheduleTarget(p)}
          />
        )}

        {imageEditTarget && (
          <ProductImageEditModal
            product={imageEditTarget}
            onClose={() => setImageEditTarget(null)}
            onSaveUrl={(url) => saveImageUrl(imageEditTarget.id, url)}
            onUploadFile={(file) => uploadImageFile(imageEditTarget.id, file)}
          />
        )}

        {scheduleTarget && (
          <ProductScheduleModal
            product={scheduleTarget}
            onClose={() => setScheduleTarget(null)}
            onSave={(launchAt, archiveAt) => {
              saveSchedule(scheduleTarget.id, launchAt, archiveAt);
              setScheduleTarget(null);
            }}
          />
        )}

        {refulfillState.open && (
          <div
            data-testid="refulfill-confirm-modal"
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => !refulfillState.busy && setRefulfillState({ open: false, pending: 0, alreadyDone: 0, busy: false })}
          >
            <div
              className="bg-white w-full max-w-md border border-[#C5A059] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 px-5 py-3 bg-[#0A192F] text-[#D4AF37]">
                <PackageCheck size={16} />
                <span className="font-accent text-xs tracking-wider">RE-FULFILL PRE-ORDER CUSTOMERS</span>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-sm text-slate-700 leading-relaxed">
                  This will re-send the final <strong>29-page PDF</strong> to{" "}
                  <strong className="text-[#0A192F]">{refulfillState.pending}</strong> pre-order customer(s) who paid before launch.
                </p>
                {refulfillState.alreadyDone > 0 && (
                  <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 px-3 py-2">
                    <span className="font-accent text-[10px] text-[#C5A059]">ALREADY RE-FULFILLED:</span>{" "}
                    {refulfillState.alreadyDone} customer(s) will be skipped.
                  </p>
                )}
                {refulfillState.pending === 0 ? (
                  <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2">
                    No pending pre-order customers to re-fulfill.
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Emails will be sent with a 5-second throttle to respect MailerSend rate limits.
                    Estimated send time: <strong>{Math.max(1, Math.round(refulfillState.pending * 5 / 60))} min</strong>.
                  </p>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    data-testid="refulfill-cancel-btn"
                    onClick={() => setRefulfillState({ open: false, pending: 0, alreadyDone: 0, busy: false })}
                    disabled={refulfillState.busy}
                    className="px-4 py-2 text-xs font-accent text-slate-600 hover:text-[#0A192F] border border-slate-300 disabled:opacity-50"
                  >
                    CANCEL
                  </button>
                  <button
                    data-testid="refulfill-confirm-btn"
                    onClick={executeRefulfill}
                    disabled={refulfillState.busy || refulfillState.pending === 0}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-accent text-white bg-[#0A192F] hover:bg-[#0A192F]/90 disabled:opacity-50 border border-[#C5A059] hover:border-[#D4AF37]"
                  >
                    {refulfillState.busy ? (
                      <><Loader2 size={12} className="animate-spin text-[#D4AF37]" /> SENDING…</>
                    ) : (
                      <><Send size={12} className="text-[#D4AF37]" /> PROCEED — SEND {refulfillState.pending} EMAILS</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "marketing" && (
          <MarketingTab />
        )}

        {tab === "audit" && (
          <AuditLogTab />
        )}

        {tab === "carts" && (
          <div data-testid="abandoned-carts">
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <MiniStat label="PENDING" value={carts.counts?.pending ?? 0} />
              <MiniStat label="REMINDED" value={carts.counts?.reminded ?? 0} />
              <MiniStat label="RECOVERED" value={carts.counts?.recovered ?? 0} />
            </div>

            <div className="bg-white border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="px-5 py-3 font-accent text-[10px]">STARTED</th>
                    <th className="px-5 py-3 font-accent text-[10px]">EMAIL</th>
                    <th className="px-5 py-3 font-accent text-[10px]">PRODUCT</th>
                    <th className="px-5 py-3 font-accent text-[10px]">STATUS</th>
                    <th className="px-5 py-3 font-accent text-[10px]">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {(!carts.carts || carts.carts.length === 0) && (
                    <tr>
                      <td colSpan="5" className="px-5 py-12 text-center text-slate-400">
                        No abandoned carts yet.
                      </td>
                    </tr>
                  )}
                  {(carts.carts || []).map((c) => {
                    let pill = ["bg-amber-50 text-amber-700", "PENDING"];
                    if (c.recovered_at) pill = ["bg-emerald-50 text-emerald-700", "RECOVERED"];
                    else if (c.reminder_sent_at) pill = ["bg-sky-50 text-sky-700", "REMINDED"];
                    return (
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="px-5 py-3 text-slate-600">
                          {new Date(c.created_at).toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-slate-600">
                          <div>{c.customer_email}</div>
                          <div className="text-xs text-slate-400">{c.customer_name || ""}</div>
                        </td>
                        <td className="px-5 py-3 text-[#0A192F] font-medium">{c.product_name}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-block px-2.5 py-1 text-[10px] font-accent ${pill[0]}`}>
                            {pill[1]}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          {!c.recovered_at && (
                            <div className="flex flex-wrap gap-1.5">
                              {!c.reminder_sent_at && (
                                <button
                                  data-testid={`send-recovery-${c.id}`}
                                  onClick={async () => {
                                    try {
                                      const { data } = await adminApi.post(
                                        `/admin/abandoned-carts/${c.id}/send-recovery`
                                      );
                                      if (data.sent) toast.success("First reminder sent.");
                                      else
                                        toast.message(
                                          data.reason || "Skipped — MailerSend may not be configured."
                                        );
                                      load();
                                    } catch (e) {
                                      toast.error("Failed to send.");
                                    }
                                  }}
                                  className="inline-flex items-center gap-1.5 text-[11px] font-accent text-[#C5A059] hover:text-[#0A192F] border border-[#C5A059]/40 hover:border-[#0A192F] px-2.5 py-1.5"
                                >
                                  <Send className="w-3 h-3" />
                                  24H NUDGE
                                </button>
                              )}
                              {c.reminder_sent_at && !c.second_reminder_sent_at && (
                                <button
                                  data-testid={`send-lastchance-${c.id}`}
                                  onClick={async () => {
                                    try {
                                      const { data } = await adminApi.post(
                                        `/admin/abandoned-carts/${c.id}/send-last-chance`
                                      );
                                      if (data.sent) toast.success(`Coupon ${data.coupon_code} issued.`);
                                      else
                                        toast.message(
                                          data.coupon_code
                                            ? `Coupon ${data.coupon_code} created (email not sent).`
                                            : "Skipped — see logs."
                                        );
                                      load();
                                    } catch (e) {
                                      toast.error("Failed to send.");
                                    }
                                  }}
                                  className="inline-flex items-center gap-1.5 text-[11px] font-accent text-rose-700 hover:text-white hover:bg-rose-700 border border-rose-700/40 hover:border-rose-700 px-2.5 py-1.5"
                                >
                                  <Send className="w-3 h-3" />
                                  LAST CHANCE
                                </button>
                              )}
                              {c.second_reminder_coupon_code && (
                                <span className="inline-flex items-center text-[10px] font-mono text-[#0A192F] border border-[#C5A059]/40 bg-[#C5A059]/10 px-2 py-1.5">
                                  {c.second_reminder_coupon_code}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <ProductCreateModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onCreated={load}
      />
    </div>
  );
}

const MiniStat = ({ label, value }) => (
  <div className="bg-white border border-slate-200 p-4">
    <div className="font-accent text-[10px] text-slate-500">{label}</div>
    <div className="font-display text-xl text-[#0A192F] mt-1">{value}</div>
  </div>
);

const StatCard = ({ label, value, icon: Icon, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 p-6 flex items-center justify-between">
    <div>
      <div className="font-accent text-[10px] text-slate-500 mb-2">{label}</div>
      <div className="font-display text-2xl text-[#0A192F]">{value}</div>
    </div>
    <div className="w-11 h-11 bg-[#0A192F] flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#C5A059]" />
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    paid: ["bg-emerald-50 text-emerald-700", "PAID"],
    completed_mock: ["bg-emerald-50 text-emerald-700", "PAID (MOCK)"],
    initiated: ["bg-amber-50 text-amber-700", "PENDING"],
    mock_pending: ["bg-amber-50 text-amber-700", "MOCK"],
    expired: ["bg-rose-50 text-rose-700", "EXPIRED"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-600", status?.toUpperCase() || "—"];
  return (
    <span className={`inline-block px-2.5 py-1 text-[10px] font-accent ${cls}`}>{label}</span>
  );
};

const AnalyticsPanel = ({ data, loading, pending, onSendReminder, sendingReminder, onResetFailed }) => {
  if (loading && !data) {
    return (
      <div data-testid="analytics-loading" className="bg-white border border-slate-200 p-12 text-center text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-[#C5A059]" />
        Loading analytics…
      </div>
    );
  }
  if (!data) {
    return (
      <div data-testid="analytics-empty" className="bg-white border border-slate-200 p-12 text-center text-slate-400">
        No analytics data yet.
      </div>
    );
  }

  const chartData = (data.top_products || []).slice(0, 8).map((p) => ({
    name: p.product_name,
    downloads: p.downloads,
  }));
  const hasChart = chartData.length > 0;
  const dh = data.delivery_health || {};
  const showAlert = dh.status === "warning" || dh.status === "critical";

  return (
    <div data-testid="analytics-panel" className="space-y-5">
      {showAlert && (
        <div
          data-testid="delivery-health-alert"
          className={`border-l-4 p-4 flex items-start gap-3 ${
            dh.status === "critical"
              ? "bg-rose-50 border-rose-500 text-rose-800"
              : "bg-amber-50 border-amber-500 text-amber-800"
          }`}
        >
          <div className="font-display text-xl leading-none mt-0.5">!</div>
          <div className="flex-1 min-w-0">
            <div className="font-accent text-[10px] tracking-[0.25em]">
              {dh.status === "critical" ? "CRITICAL" : "DELIVERY WARNING"}
            </div>
            <div className="mt-1 text-sm font-semibold">
              {dh.failed_count} download reminder{dh.failed_count === 1 ? "" : "s"} failed delivery
              {dh.capped_count > 0 && (
                <> · <span className="underline">{dh.capped_count} hit the {dh.max_attempts}-attempt cap</span></>
              )}
            </div>
            {dh.last_error && (
              <div className="mt-1.5 text-xs opacity-80 break-words">
                <span className="font-accent tracking-wider">LAST ERROR · </span>
                {dh.last_error.length > 160 ? dh.last_error.slice(0, 160) + "…" : dh.last_error}
              </div>
            )}
            <div className="mt-2 text-xs opacity-80">
              Most common cause: MailerSend domain unverified or daily quota hit. Once green, click <span className="font-bold">RE-ARM FAILED</span> below to retry.
            </div>
          </div>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard
          label="TOTAL DOWNLOADS"
          value={data.total_downloads ?? 0}
          icon={Download}
          testid="analytics-total-downloads"
        />
        <StatCard
          label="PAY → DOWNLOAD"
          value={`${data.conversion_rate_pct ?? 0}%`}
          icon={BarChart3}
          testid="analytics-conversion"
        />
        <StatCard
          label="ORDERS THAT DOWNLOADED"
          value={`${data.unique_orders_downloaded ?? 0} / ${data.paid_orders ?? 0}`}
          icon={PackageCheck}
          testid="analytics-unique-orders"
        />
      </div>

      {/* Top plans bar chart */}
      <div data-testid="analytics-top-plans" className="bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-accent text-[10px] text-slate-500">TOP PLANS BY DOWNLOADS</div>
            <div className="font-display text-lg text-[#0A192F] mt-0.5">
              What customers are actually consuming
            </div>
          </div>
        </div>
        {hasChart ? (
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 56 }}>
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#475569" }}
                  interval={0}
                  angle={-22}
                  textAnchor="end"
                  height={70}
                />
                <YAxis tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(197,160,89,0.08)" }}
                  contentStyle={{
                    background: "#0A192F",
                    border: "1px solid #C5A059",
                    color: "#FFFFFF",
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "#C5A059", fontWeight: 600 }}
                />
                <Bar dataKey="downloads" radius={[2, 2, 0, 0]}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "#C5A059" : "#0A192F"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400 text-sm">
            No downloads recorded yet. Once customers fetch their files this chart will fill in.
          </div>
        )}
      </div>

      {/* Top files + Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div data-testid="analytics-top-files" className="bg-white border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="font-accent text-[10px] text-slate-500">TOP FILES</div>
          </div>
          {(data.top_files || []).length === 0 ? (
            <div className="px-5 py-10 text-center text-slate-400 text-sm">No file downloads yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">FILE</th>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">PLAN</th>
                  <th className="px-4 py-2 text-right font-accent text-[10px]">DLs</th>
                </tr>
              </thead>
              <tbody>
                {data.top_files.map((f) => (
                  <tr key={f.file_id} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-[#0A192F] truncate max-w-[220px]">{f.filename}</td>
                    <td className="px-4 py-2 text-slate-600 text-xs">{f.product_name}</td>
                    <td className="px-4 py-2 text-right font-display text-[#0A192F]">{f.downloads}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div data-testid="analytics-recent" className="bg-white border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="font-accent text-[10px] text-slate-500">RECENT DOWNLOAD ACTIVITY</div>
          </div>
          {(data.recent || []).length === 0 ? (
            <div className="px-5 py-10 text-center text-slate-400 text-sm">No activity yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">WHEN</th>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">CUSTOMER</th>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">PLAN</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r, i) => (
                  <tr key={`${r.timestamp}-${i}`} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-600 whitespace-nowrap text-xs">
                      {r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2 text-slate-600 truncate max-w-[180px]">
                      {r.customer_email || "—"}
                    </td>
                    <td className="px-4 py-2 text-[#0A192F] text-xs">
                      {r.product_name}
                      {r.event_type === "zip" && (
                        <span className="ml-1.5 inline-block px-1.5 py-0.5 text-[9px] tracking-wider bg-[#C5A059]/15 text-[#C5A059]">
                          ZIP
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pending download reminders — paid orders that never downloaded */}
      <div data-testid="analytics-pending-reminders" className="bg-white border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-accent text-[10px] text-slate-500">PAID · NOT YET DOWNLOADED</div>
            <div className="font-display text-base text-[#0A192F] mt-0.5">
              Customers who paid but haven't grabbed their files
            </div>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-3">
            <span>{pending?.count || 0} candidate{(pending?.count || 0) === 1 ? "" : "s"} · auto-emailed at 48h</span>
            <button
              type="button"
              data-testid="reset-failed-reminders-btn"
              onClick={onResetFailed}
              className="inline-flex items-center gap-1 text-[10px] font-accent text-[#C5A059] border border-[#C5A059]/40 hover:border-[#0A192F] hover:text-[#0A192F] px-2.5 py-1"
              title="Re-arm reminders that MailerSend previously rejected (e.g. before domain verification)"
            >
              <RefreshCw className="w-3 h-3" />
              RE-ARM FAILED
            </button>
          </div>
        </div>
        {!pending?.pending?.length ? (
          <div className="px-5 py-10 text-center text-slate-400 text-sm">
            Everyone who paid has downloaded. Nice work.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">PURCHASED</th>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">CUSTOMER</th>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">PLAN</th>
                  <th className="px-4 py-2 text-right font-accent text-[10px]">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {pending.pending.slice(0, 15).map((r) => {
                  const hoursAgo = r.created_at
                    ? Math.floor((Date.now() - new Date(r.created_at).getTime()) / 3600000)
                    : null;
                  const isReady = hoursAgo !== null && hoursAgo >= 48;
                  const isSending = sendingReminder === r.order_id;
                  return (
                    <tr key={r.order_id} className="border-t border-slate-100">
                      <td className="px-4 py-2 text-slate-600 whitespace-nowrap text-xs">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                        <span className="ml-1.5 text-slate-400">
                          ({hoursAgo !== null ? `${hoursAgo}h ago` : "—"})
                        </span>
                      </td>
                      <td className="px-4 py-2 text-slate-700 truncate max-w-[200px]">
                        {r.customer_email}
                        <div className="text-xs text-slate-400">{r.customer_name || ""}</div>
                      </td>
                      <td className="px-4 py-2 text-[#0A192F] text-xs">{r.product_name}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          data-testid={`send-download-reminder-${r.order_id}`}
                          disabled={isSending}
                          onClick={() => onSendReminder?.(r.order_id)}
                          className={`inline-flex items-center gap-1.5 text-[11px] font-accent px-2.5 py-1.5 border ${
                            isReady
                              ? "text-[#C5A059] border-[#C5A059]/40 hover:text-[#0A192F] hover:border-[#0A192F]"
                              : "text-slate-500 border-slate-300 hover:text-[#0A192F] hover:border-[#0A192F]"
                          } disabled:opacity-50`}
                          title={isReady ? "48h+ window reached" : "Below 48h — admin override"}
                        >
                          {isSending ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Send className="w-3 h-3" />
                          )}
                          {isSending ? "SENDING…" : "SEND REMINDER"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {pending.pending.length > 15 && (
              <div className="px-5 py-2 text-[11px] text-slate-400 border-t border-slate-100">
                Showing first 15 of {pending.pending.length}. The 48h scheduler will email the rest automatically.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


const formatUptime = (sec) => {
  if (sec == null) return "—";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${sec % 60}s`;
};

const SystemBadge = ({ status }) => {
  const map = {
    ok: ["bg-emerald-50 text-emerald-700", "OK"],
    warning: ["bg-amber-50 text-amber-700", "WARNING"],
    critical: ["bg-rose-50 text-rose-700", "CRITICAL"],
    live: ["bg-emerald-50 text-emerald-700", "LIVE"],
    sandbox: ["bg-amber-50 text-amber-700", "SANDBOX"],
    test: ["bg-amber-50 text-amber-700", "TEST"],
    mocked: ["bg-rose-50 text-rose-700", "MOCKED"],
    missing: ["bg-rose-50 text-rose-700", "MISSING"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-600", String(status || "—").toUpperCase()];
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-accent tracking-wider ${cls}`}>{label}</span>;
};

const SystemPanel = ({ data, loading, onReload, emailStats, bounceRate }) => {
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try { return localStorage.getItem("aigl_system_autorefresh") === "1"; } catch { return false; }
  });
  const [countdown, setCountdown] = useState(30);

  useEffect(() => {
    try { localStorage.setItem("aigl_system_autorefresh", autoRefresh ? "1" : "0"); } catch { /* noop */ }
    if (!autoRefresh) {
      setCountdown(30);
      return;
    }
    setCountdown(30);
    const tick = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          onReload?.();
          return 30;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [autoRefresh, onReload]);

  if (loading && !data) {
    return (
      <div data-testid="system-loading" className="bg-white border border-slate-200 p-12 text-center text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-[#C5A059]" />
        Loading system status…
      </div>
    );
  }
  if (!data) {
    return <div data-testid="system-empty" className="bg-white border border-slate-200 p-12 text-center text-slate-400">No system data.</div>;
  }
  const { app, integrations, scheduler, collections, queue, delivery } = data;

  return (
    <div data-testid="system-panel" className="space-y-5">
      <BounceRateBanner data={bounceRate} />

      {/* App + delivery summary */}
      <div className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-accent text-[10px] text-slate-500">SERVICE STATUS</div>
            <div className="font-display text-lg text-[#0A192F] mt-0.5 flex items-center gap-2">
              {app.service}
              <SystemBadge status={delivery.status} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="system-autorefresh-toggle"
              onClick={() => setAutoRefresh((v) => !v)}
              className={`inline-flex items-center gap-1.5 text-[11px] font-accent px-2.5 py-1.5 border transition ${
                autoRefresh
                  ? "bg-[#C5A059]/15 border-[#C5A059] text-[#0A192F]"
                  : "border-slate-300 text-slate-600 hover:border-[#0A192F] hover:text-[#0A192F]"
              }`}
              title="Auto-refresh every 30 seconds"
            >
              <span className={`w-2 h-2 rounded-full ${autoRefresh ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
              AUTO-REFRESH {autoRefresh ? `· ${countdown}s` : "OFF"}
            </button>
            <button
              type="button"
              data-testid="system-reload"
              onClick={onReload}
              className="inline-flex items-center gap-1.5 text-[11px] font-accent text-[#0A192F] border border-slate-300 hover:border-[#0A192F] px-2.5 py-1.5"
            >
              <RefreshCw className="w-3 h-3" /> REFRESH
            </button>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-px bg-slate-100">
          <KvCell label="Uptime" value={formatUptime(app.uptime_seconds)} />
          <KvCell label="Server Time" value={new Date(app.now).toLocaleTimeString()} />
          <KvCell label="Started" value={new Date(app.started_at).toLocaleString()} />
        </div>
      </div>

      {/* Integrations */}
      <div className="bg-white border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="font-accent text-[10px] text-slate-500">INTEGRATIONS</div>
        </div>
        <div className="grid sm:grid-cols-3 gap-px bg-slate-100">
          <div data-testid="int-paypal" className="bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="font-display text-[#0A192F]">PayPal</div>
              <SystemBadge status={integrations.paypal.mode} />
            </div>
            <div className="text-xs text-slate-500 mt-1">mode</div>
          </div>
          <div data-testid="int-stripe" className="bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="font-display text-[#0A192F]">Stripe</div>
              <SystemBadge status={integrations.stripe.mode} />
            </div>
            <div className="text-xs text-slate-500 mt-1">mode</div>
          </div>
          <div data-testid="int-mailersend" className="bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="font-display text-[#0A192F]">MailerSend</div>
              <SystemBadge status={integrations.mailersend.branded_sender ? "live" : "warning"} />
            </div>
            <div className="text-xs text-slate-500 mt-1 break-all">
              from: <span className="text-slate-700">{integrations.mailersend.sender}</span>
            </div>
            <div className="text-xs text-slate-500 break-all">
              reply-to: <span className="text-slate-700">{integrations.mailersend.reply_to}</span>
            </div>
            {!integrations.mailersend.branded_sender && (
              <div className="text-[11px] text-amber-700 mt-2">
                Domain unverified — sending from generic fallback.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email Activity */}
      <EmailActivityPanel stats={emailStats} />


      {/* Queue + Delivery */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div data-testid="queue-card" className="bg-white border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="font-accent text-[10px] text-slate-500">QUEUE DEPTH</div>
          </div>
          <div className="divide-y divide-slate-100">
            <KvRow label="Paid orders" value={queue.paid_orders} />
            <KvRow label="Pending download reminders" value={queue.pending_download_reminders} accent={queue.pending_download_reminders > 0} />
            <KvRow label="Pending 24h cart nudges" value={queue.pending_carts_24h} />
            <KvRow label="Pending 72h last-chance" value={queue.pending_carts_72h} />
          </div>
        </div>

        <div data-testid="delivery-card" className="bg-white border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="font-accent text-[10px] text-slate-500">DELIVERY HEALTH</div>
            <SystemBadge status={delivery.status} />
          </div>
          <div className="divide-y divide-slate-100">
            <KvRow label="Failed reminders" value={delivery.failed_count} accent={delivery.failed_count > 0} />
            <KvRow label={`Hit ${scheduler.download_reminder_max_attempts}-attempt cap`} value={delivery.capped_count} accent={delivery.capped_count > 0} />
            <KvRow
              label="Last failure"
              value={delivery.last_failure_at ? new Date(delivery.last_failure_at).toLocaleString() : "—"}
            />
          </div>
          {delivery.last_error && (
            <div className="px-5 py-3 bg-slate-50 text-[11px] text-slate-700 break-words">
              <span className="font-accent tracking-wider text-slate-500">LAST ERROR · </span>
              {delivery.last_error.length > 200 ? delivery.last_error.slice(0, 200) + "…" : delivery.last_error}
            </div>
          )}
        </div>
      </div>

      {/* Scheduler config + DB */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div data-testid="scheduler-card" className="bg-white border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="font-accent text-[10px] text-slate-500">SCHEDULER CONFIG</div>
          </div>
          <div className="divide-y divide-slate-100">
            <KvRow label="Poll interval" value={`${scheduler.poll_seconds}s`} />
            <KvRow label="Send throttle" value={`${scheduler.send_throttle_seconds}s`} />
            <KvRow label="24h cart nudge" value={`${scheduler.first_reminder_hours}h`} />
            <KvRow label="72h last-chance" value={`${scheduler.last_chance_hours}h`} />
            <KvRow label="Download reminder" value={`${scheduler.download_reminder_hours}h`} />
            <KvRow label="Reminder cooldown" value={`${scheduler.download_reminder_cooldown_hours}h between retries`} />
            <KvRow label="Reminder max attempts" value={scheduler.download_reminder_max_attempts} />
          </div>
        </div>

        <div data-testid="db-card" className="bg-white border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="font-accent text-[10px] text-slate-500">DATABASE</div>
          </div>
          <div className="divide-y divide-slate-100">
            {Object.entries(collections).map(([k, v]) => (
              <KvRow key={k} label={k} value={v} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const KvCell = ({ label, value }) => (
  <div className="bg-white p-4">
    <div className="font-accent text-[10px] text-slate-500">{label.toUpperCase()}</div>
    <div className="font-display text-base text-[#0A192F] mt-1">{value}</div>
  </div>
);

const KvRow = ({ label, value, accent }) => (
  <div className="flex items-center justify-between px-5 py-2.5 text-sm">
    <span className="text-slate-600">{label}</span>
    <span className={`font-display ${accent ? "text-[#C5A059]" : "text-[#0A192F]"}`}>{value}</span>
  </div>
);


const EmailActivityPanel = ({ stats }) => {
  if (!stats) {
    return (
      <div data-testid="email-activity-panel" className="bg-white border border-slate-200 p-8 text-center text-slate-400 text-sm">
        Email activity will appear here once the MailerSend webhook receives events.
      </div>
    );
  }
  const t = stats.totals || {};
  const w = (stats.windowed && stats.windowed["24h"]) || {};
  const r = stats.rates || {};
  const recent = stats.recent || [];

  const cards = [
    { key: "delivered", label: "DELIVERED", value: t.delivered, last24: w.delivered, icon: Mail, accent: "text-emerald-600", bg: "bg-emerald-500/10" },
    { key: "opened", label: "OPENED", value: t.opened, last24: w.opened, icon: MailOpen, accent: "text-[#D4AF37]", bg: "bg-[#D4AF37]/15", trailing: r.open_rate_pct ? `${r.open_rate_pct}% open rate` : null },
    { key: "clicked", label: "LINK CLICKS", value: t.clicked, last24: w.clicked, icon: MousePointerClick, accent: "text-sky-600", bg: "bg-sky-500/10", trailing: r.click_rate_pct ? `${r.click_rate_pct}% click rate` : null },
    { key: "bounced", label: "BOUNCED / FAILED", value: t.bounced, last24: w.bounced, icon: AlertTriangle, accent: "text-rose-600", bg: "bg-rose-500/10", trailing: r.bounce_rate_pct ? `${r.bounce_rate_pct}% bounce rate` : null },
  ];

  return (
    <div data-testid="email-activity-panel" className="bg-white border border-slate-200">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-accent text-[10px] text-slate-500">EMAIL ACTIVITY</div>
          <div className="font-display text-base text-[#0A192F] mt-0.5">
            MailerSend webhook events · {stats.total_events ?? 0} total
          </div>
        </div>
        <div className="text-[11px] text-slate-500">Lifetime totals · last 24h in parentheses</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-100">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} data-testid={`email-stat-${c.key}`} className="bg-white p-5 flex flex-col">
              <div className={`w-10 h-10 flex items-center justify-center ${c.bg} ${c.accent} mb-3`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="font-accent text-[10px] tracking-[0.25em] text-slate-500">{c.label}</div>
              <div className="font-display text-2xl text-[#0A192F] mt-1">
                {(c.value ?? 0).toLocaleString()}
                <span className="ml-1.5 text-xs text-slate-400">({(c.last24 ?? 0).toLocaleString()})</span>
              </div>
              {c.trailing && <div className="mt-1 text-[11px] text-slate-500">{c.trailing}</div>}
            </div>
          );
        })}
      </div>

      {/* Recent events */}
      {recent.length > 0 && (
        <div className="border-t border-slate-100">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="font-accent text-[10px] text-slate-500">LATEST {Math.min(recent.length, 20)} EVENTS</div>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">WHEN</th>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">EVENT</th>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">RECIPIENT</th>
                  <th className="px-4 py-2 text-left font-accent text-[10px]">SUBJECT</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((ev, i) => (
                  <tr key={`${ev.timestamp}-${i}`} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-xs text-slate-600 whitespace-nowrap">
                      {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2"><EventBadge type={ev.event_type} /></td>
                    <td className="px-4 py-2 text-xs text-slate-700 truncate max-w-[220px]">{ev.email || "—"}</td>
                    <td className="px-4 py-2 text-xs text-[#0A192F] truncate max-w-[260px]">{ev.subject || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const EventBadge = ({ type }) => {
  const short = (type || "").replace("activity.", "");
  const map = {
    delivered: "bg-emerald-50 text-emerald-700",
    opened: "bg-[#D4AF37]/15 text-[#0A192F]",
    clicked: "bg-sky-50 text-sky-700",
    hard_bounced: "bg-rose-50 text-rose-700",
    soft_bounced: "bg-amber-50 text-amber-700",
    spam_complaint: "bg-rose-50 text-rose-800",
    unsubscribed: "bg-slate-100 text-slate-600",
  };
  const cls = map[short] || "bg-slate-100 text-slate-600";
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-accent tracking-wider ${cls}`}>{short.toUpperCase()}</span>;
};


const BounceRateBanner = ({ data }) => {
  if (!data) return null;
  const rate = Number(data.rate_pct ?? 0);
  const threshold = Number(data.threshold_pct ?? 5);
  const sample = data.sample_size ?? 0;
  const hard = data.hard_bounces ?? 0;
  const isAlert = !!data.alert_active;

  if (!isAlert) {
    // Healthy state — small green pill
    return (
      <div
        data-testid="bounce-rate-healthy"
        className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs font-accent text-emerald-700"
      >
        <span className="w-2 h-2 rounded-full bg-emerald-500" />
        BOUNCE RATE: {rate.toFixed(1)}% ✓
        <span className="text-emerald-500/80">(threshold {threshold}%)</span>
      </div>
    );
  }

  return (
    <div
      data-testid="bounce-rate-alert"
      className="border-l-[6px] border-rose-600 bg-gradient-to-r from-rose-600 to-rose-700 text-white shadow-lg"
    >
      <div className="flex items-start gap-4 p-5">
        <div className="font-display text-3xl leading-none mt-0.5" aria-hidden="true">⚠️</div>
        <div className="flex-1 min-w-0">
          <div className="font-accent text-[10px] tracking-[0.3em] text-rose-100">
            HIGH BOUNCE RATE ALERT
          </div>
          <div className="mt-1 font-display text-lg sm:text-xl leading-tight">
            {rate.toFixed(1)}% hard bounces detected
            <span className="ml-2 text-rose-200 text-sm font-normal">
              ({hard} of last {sample} events · threshold {threshold}%)
            </span>
          </div>
          <p className="mt-2 text-sm text-white/90 leading-relaxed max-w-2xl">
            Your domain reputation is at risk. Review bounced addresses immediately and pause any
            non-transactional sends until the rate falls below threshold.
          </p>
          {data.bounced_addresses && data.bounced_addresses.length > 0 && (
            <details className="mt-3 text-xs text-rose-100">
              <summary className="cursor-pointer font-accent tracking-wider hover:text-white">
                SHOW {data.bounced_addresses.length} BOUNCED ADDRESSES
              </summary>
              <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 font-mono">
                {data.bounced_addresses.slice(0, 50).map((e) => (
                  <li key={e} className="truncate">{e}</li>
                ))}
              </ul>
            </details>
          )}
          {data.last_alert_sent_at && (
            <div className="mt-3 text-[11px] text-rose-200">
              Last ops email alert sent {new Date(data.last_alert_sent_at).toLocaleString()} · recipient: {data.alert_recipient}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const ReviewsPanel = ({ data, onApprove, onReject, onSendPrompt, orders }) => {
  const [filter, setFilter] = useState("pending");
  const reviews = (data?.reviews || []).filter((r) =>
    filter === "all" ? true : r.status === filter
  );
  const counts = data?.counts || { pending: 0, approved: 0, rejected: 0 };

  const promptCandidates = (orders || [])
    .filter((o) => ["paid", "completed_mock"].includes(o.payment_status))
    .filter((o) => o.first_download_at && !o.review_prompt_sent_at)
    .slice(0, 20);

  return (
    <div data-testid="reviews-panel" className="space-y-5">
      <div className="grid sm:grid-cols-4 gap-4">
        <MiniStat label="PENDING" value={counts.pending || 0} />
        <MiniStat label="APPROVED" value={counts.approved || 0} />
        <MiniStat label="REJECTED" value={counts.rejected || 0} />
        <MiniStat label="PROMPT-READY" value={promptCandidates.length} />
      </div>

      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`reviews-filter-${f}`}
            className={`font-accent text-[10px] tracking-[2px] px-3 py-2 transition ${
              filter === f
                ? "bg-[#0A192F] text-white"
                : "bg-white border border-slate-300 text-slate-500 hover:text-[#0A192F]"
            }`}
          >
            {f.toUpperCase()}{" "}
            {f !== "all" && counts[f] !== undefined && (
              <span className="opacity-60">({counts[f]})</span>
            )}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200">
        {reviews.length === 0 ? (
          <div className="px-5 py-16 text-center text-slate-400 text-sm" data-testid="reviews-empty">
            No reviews in this view yet. Customer reviews appear here 72h after a paid buyer
            downloads their plan — and submits the form from their email link.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-5 py-3 font-accent text-[10px]">RATING</th>
                <th className="px-5 py-3 font-accent text-[10px]">CUSTOMER</th>
                <th className="px-5 py-3 font-accent text-[10px]">PRODUCT</th>
                <th className="px-5 py-3 font-accent text-[10px]">QUOTE</th>
                <th className="px-5 py-3 font-accent text-[10px]">STATUS</th>
                <th className="px-5 py-3 font-accent text-[10px] text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((r, idx) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`review-row-${idx}`}>
                  <td className="px-5 py-3 whitespace-nowrap">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <span
                          key={n}
                          className={`text-[15px] leading-none ${
                            n <= r.rating ? "text-[#C5A059]" : "text-slate-300"
                          }`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">
                      {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-[#0A192F]">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.customer_email}</div>
                  </td>
                  <td className="px-5 py-3 text-slate-600 text-xs">{r.product_name || r.product_id}</td>
                  <td className="px-5 py-3 text-slate-700 text-[13px] max-w-md">
                    <p className="line-clamp-3">&ldquo;{r.quote}&rdquo;</p>
                  </td>
                  <td className="px-5 py-3">
                    <ReviewStatusPill status={r.status} />
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {r.status !== "approved" && (
                      <button
                        onClick={() => onApprove(r.id)}
                        data-testid={`approve-review-${idx}`}
                        className="text-xs font-accent tracking-wider text-emerald-700 hover:text-emerald-900 mr-3"
                      >
                        APPROVE
                      </button>
                    )}
                    {r.status !== "rejected" && (
                      <button
                        onClick={() => onReject(r.id)}
                        data-testid={`reject-review-${idx}`}
                        className="text-xs font-accent tracking-wider text-rose-600 hover:text-rose-800"
                      >
                        REJECT
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {promptCandidates.length > 0 && (
        <div className="bg-white border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="font-accent text-[10px] tracking-[2px] text-slate-500">
              MANUAL REVIEW-PROMPT TRIGGER
            </div>
            <div className="text-xs text-slate-500 mt-1">
              These paid customers downloaded their files but haven't been asked for a review yet.
              The scheduler will email them automatically 72h after first download — or send manually below.
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-5 py-3 font-accent text-[10px]">CUSTOMER</th>
                <th className="px-5 py-3 font-accent text-[10px]">PRODUCT</th>
                <th className="px-5 py-3 font-accent text-[10px]">DOWNLOADED</th>
                <th className="px-5 py-3 font-accent text-[10px] text-right">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {promptCandidates.map((o, idx) => (
                <tr key={o.id} className="border-t border-slate-100">
                  <td className="px-5 py-3">
                    <div>{o.customer_email || "—"}</div>
                    <div className="text-xs text-slate-400">{o.customer_name}</div>
                  </td>
                  <td className="px-5 py-3 text-slate-600 text-xs">{o.product_name}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">
                    {new Date(o.first_download_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => onSendPrompt(o.id)}
                      data-testid={`send-prompt-${idx}`}
                      className="text-xs font-accent tracking-wider text-[#C5A059] hover:text-[#D4AF37]"
                    >
                      SEND NOW
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ReviewStatusPill = ({ status }) => {
  const cfg = {
    pending: { bg: "bg-amber-100", text: "text-amber-700", label: "PENDING" },
    approved: { bg: "bg-emerald-100", text: "text-emerald-700", label: "APPROVED" },
    rejected: { bg: "bg-rose-100", text: "text-rose-700", label: "REJECTED" },
  }[status] || { bg: "bg-slate-100", text: "text-slate-700", label: status?.toUpperCase() };
  return (
    <span className={`inline-block px-2 py-1 text-[10px] font-accent tracking-wider ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
};
